import { useCallback, useEffect, useRef } from 'react'
import { useAtom } from 'jotai'
import { useDispatch, useSelector } from 'react-redux'
import { walletBalanceAtom, balanceSyncStatusAtom, lastBalanceFetchAtom } from 'store/wallet.atom'
import { getWalletBalanceSilentService } from 'network/services/users.service'
import { setSelectedWallet, setUserData } from 'redux-thunk/redux/slices/user.slice'

const POLL_INTERVAL_MS = 10000 // 10 seconds
// Minimum gap between fetches — prevents rapid-fire triggers from multiple events
const MIN_FETCH_GAP_MS = 1500

// ── Module-level singleton ──────────────────────────────────────────────────
// All hook instances share one timer and one in-flight fetch guard.
let _activeTimer   = null
let _activeFetch   = false
let _listeners     = 0
let _lastFetch     = 0

// Live refs kept up-to-date by the active hook instance
let _dispatch          = null
let _setWalletBalance  = null
let _setSyncStatus     = null
let _setLastFetch      = null
let _getToken          = null
let _getUser           = null

// Callbacks registered by consumers (e.g. game components)
const _callbacks = new Set()

// ── Core poll function ──────────────────────────────────────────────────────
const _poll = async (force = false) => {
  const token = _getToken?.()
  if (!token) return
  if (_activeFetch) return

  const now = Date.now()
  if (!force && now - _lastFetch < MIN_FETCH_GAP_MS) return

  _activeFetch = true
  _setSyncStatus?.('syncing')

  try {
    const res = await getWalletBalanceSilentService()
    if (!res) return

    const primaryWallet = res?.wallets?.find(w => w.primary) || res?.wallets?.[0]
    if (!primaryWallet) return

    _lastFetch = Date.now()
    _setLastFetch?.(_lastFetch)
    _setWalletBalance?.(primaryWallet)
    _dispatch?.(setSelectedWallet(primaryWallet))

    const user = _getUser?.()
    if (res?.wallets && user) {
      _dispatch?.(setUserData({ ...user, wallets: res.wallets }))
    }

    _callbacks.forEach(cb => {
      try { cb(primaryWallet) } catch (_) {}
    })

    _setSyncStatus?.('idle')
  } catch (_) {
    _setSyncStatus?.('error')
  } finally {
    _activeFetch = false
  }
}

// ── Standalone trigger ──────────────────────────────────────────────────────
// Call this from anywhere (game sockets, payment callbacks, etc.) without
// needing access to the hook's return value.
export const triggerBalanceRefresh = (force = false) => _poll(force)

// ── Hook ────────────────────────────────────────────────────────────────────
export const useBalanceSync = (options = {}) => {
  const { onUpdate } = options

  const dispatch = useDispatch()
  const { loginData } = useSelector((state) => state.auth)
  const { user }      = useSelector((state) => state.user)

  const [, setWalletBalance] = useAtom(walletBalanceAtom)
  const [, setSyncStatus]    = useAtom(balanceSyncStatusAtom)
  const [, setLastFetch]     = useAtom(lastBalanceFetchAtom)

  const userRef = useRef(user)
  userRef.current = user

  // Keep module refs pointing at the latest bound values so _poll() always
  // has access to the current dispatch/setters without stale closures.
  useEffect(() => {
    _dispatch         = dispatch
    _setWalletBalance = setWalletBalance
    _setSyncStatus    = setSyncStatus
    _setLastFetch     = setLastFetch
    _getToken         = () => loginData?.accessToken
    _getUser          = () => userRef.current
  })

  // Register per-instance callback
  useEffect(() => {
    if (!onUpdate) return
    _callbacks.add(onUpdate)
    return () => _callbacks.delete(onUpdate)
  }, [onUpdate])

  // Lifecycle: start/stop the global timer
  useEffect(() => {
    if (!loginData?.accessToken) {
      setWalletBalance(null)
      setSyncStatus('idle')
      return
    }

    _listeners++
    _poll() // immediate fetch on mount / login

    if (!_activeTimer) {
      _activeTimer = setInterval(() => _poll(), POLL_INTERVAL_MS)
    }

    return () => {
      _listeners--
      if (_listeners === 0 && _activeTimer) {
        clearInterval(_activeTimer)
        _activeTimer = null
      }
    }
  }, [loginData?.accessToken])

  const refreshBalance = useCallback((force = false) => _poll(force), [])

  return { refreshBalance }
}
